import React from 'react';
import { css } from '@emotion/react';

type EmailCardProps = {
  title: string;
  image: string;
  status: 'sent' | 'scheduled' | 'draft';
  statusDetail: string;
};

const card = css`
  width: 342px;
  border-radius: 10px;

  @media (max-width: 1169px) {
    width: calc(50% - 20px);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
  background: #fff;
  overflow: hidden;
  box-shadow:
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: box-shadow var(--grn-transition-fast, 200ms) ease;

  &:hover {
    box-shadow:
      0px 2px 4px -0.1px rgba(0, 0, 0, 0.08),
      0px 4px 8px -0.36px rgba(0, 0, 0, 0.06);
  }
`;

const imageWrapper = css`
  width: 100%;
  height: 336px;
  overflow: hidden;
`;

const imageStyles = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const textSection = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 24px;
  justify-content: center;
`;

const titleStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: var(--grn-weight-medium, 500);
  font-size: 14px;
  line-height: 20px;
  color: #241a17;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  height: 24px;
  display: flex;
  align-items: center;
`;

const badgeRow = css`
  display: flex;
  align-items: center;
  height: 36px;
  width: 100%;
`;

const badgeRowInner = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const badgeBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  flex-shrink: 0;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  white-space: nowrap;
`;

const badgeSent = css`
  ${badgeBase};
  background: var(--grn-color-shade12, #251b18);
  border: 1px solid rgba(60, 50, 47, 0.2);
  color: var(--grn-color-shade2, #f8f7f7);
`;

const badgeScheduled = css`
  ${badgeBase};
  background: #dcf2cf;
  color: #1f4002;
`;

const badgeDraft = css`
  ${badgeBase};
  background: #f3f2f1;
  color: #251b18;
`;

const detailText = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const chartButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  transition: background var(--grn-transition-fast, 200ms) ease;

  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const badgeStyles = {
  sent: badgeSent,
  scheduled: badgeScheduled,
  draft: badgeDraft,
};

const badgeLabels = {
  sent: 'Sent',
  scheduled: 'Scheduled',
  draft: 'Draft',
};

export default function EmailCard({ title, image, status, statusDetail }: EmailCardProps) {
  return (
    <div css={card}>
      <div css={imageWrapper}>
        <img css={imageStyles} src={image} alt={title} />
      </div>
      <div css={textSection}>
        <p css={titleStyles}>{title}</p>
        <div css={badgeRow}>
          <div css={badgeRowInner}>
            <span css={badgeStyles[status]}>{badgeLabels[status]}</span>
            <span css={detailText}>{statusDetail}</span>
          </div>
          {status === 'sent' && (
            <button css={chartButton} type="button" aria-label="View analytics">
              <svg width="12" height="18" viewBox="0 0 12 18" fill="none">
                <rect x="0" y="10" width="2.5" height="8" rx="1" fill="currentColor" />
                <rect x="4.75" y="6" width="2.5" height="12" rx="1" fill="currentColor" />
                <rect x="9.5" y="2" width="2.5" height="16" rx="1" fill="currentColor" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
